import React from 'react'
import styled from '@emotion/styled'
import * as constants from '../../styles/constants'
import { isNil } from 'lodash'

export interface TableColumn<T> {
  colId: string
  headerName: string
  /** It doesn't accept components definition as cellRenderers, always use a function returning JSX */
  cellRenderer?: (props: {
    /** The output given by the valueGetter. */
    value: string
    data: T
    index: number
    rows: T[]
  }) => React.JSX.Element
  valueGetter?: (props: { data: T; index: number; rows: T[] }) => string
  styles?: {
    /** It only applies if autoExpandRows is not set on grid options. */
    flex?: number
    left?: boolean
    overflow?: 'visible' | 'hidden'
  }
  hide?: (props: { rows: T[] }) => boolean
}

export interface TableOptions<T> {
  getRowId: (row: T) => string
  /**
   * Boolean property to auto-manage the row spacing based on CSS Grid repeat and minmax combo.
   */
  autoExpandRows?: boolean
}

export const Table = <TRow,>({
  columns,
  rows,
  options,
  ['data-test-id']: dataTestId = 'PanelTable'
}: {
  columns: TableColumn<TRow>[]
  'data-test-id'?: string
  options: TableOptions<TRow>
  rows: TRow[]
}) => {
  return (
    <div data-test-id={dataTestId} role="table">
      <div role="rowheader">
        <TableHeader autoExpandRows={options.autoExpandRows} data-test-id={`${dataTestId}-Header`} role="row">
          {columns
            .filter(element => (element.hasOwnProperty('hide') ? !element.hide?.({ rows }) : true))
            .map(c => (
              <TableCell {...c.styles} key={c.headerName} role="columnheader">
                {c.headerName}
              </TableCell>
            ))}
        </TableHeader>
      </div>
      <TableRows data-test-id={`${dataTestId}-Rows`} role="rowgroup">
        {rows.map((row, index) => (
          <TableRow autoExpandRows={options.autoExpandRows} key={options.getRowId(row)} role="row">
            {columns
              .filter(element => (element.hasOwnProperty('hide') ? !element.hide?.({ rows }) : true))
              .map(c => (
                <TableCell {...c.styles} key={c.colId} role="gridcell">
                  {c.cellRenderer ? (
                    c.cellRenderer({ data: row, index, rows, value: c?.valueGetter?.({ data: row, index, rows }) ?? '' })
                  ) : (
                    <>{c.valueGetter ? c.valueGetter({ data: row, index, rows }) : ''}</>
                  )}
                </TableCell>
              ))}
          </TableRow>
        ))}
      </TableRows>
    </div>
  )
}

const TableRows = styled.div`
  height: 300px;
  overflow: overlay;
  margin-bottom: 10px;
  & > div:nth-child(even) {
    background-color: ${constants.CORE_PRIMARY_1};
  }
`

const TableRow = styled.div<{ autoExpandRows?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 12px 10px;
  ${({ autoExpandRows }) =>
    autoExpandRows &&
    `
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(30px, 1fr))
    `}
`

const TableHeader = styled(TableRow)`
  font-weight: ${constants.FontWeight.SemiBold};
  border-top: 2px solid ${constants.CORE_PRIMARY_1};
  border-bottom: 2px solid ${constants.CORE_PRIMARY_1};
`

const TableCell = styled.div<{ flex?: number; left?: boolean; dataTestId?: string; overflow?: 'visible' | 'hidden' }>`
  font-size: 12px;
  margin: 0 10px 0 0;
  flex: ${props => (!isNil(props.flex) ? `${props.flex} 0 0` : '')};
  display: flex;
  flex-direction: row;
  justify-content: ${props => (props.left ? 'flex-start' : 'flex-end')};
  white-space: nowrap;
  overflow: ${props => props.overflow ?? 'hidden'};
  text-overflow: ellipsis;
`
